import React, { ComponentType, Fragment } from 'react'

import _ from 'lodash'
import { FormatDate } from '../utils'

// TODO: Move these types up a level so that they can be shared between related components
export type DataViewRACAnalyst = {
  firstName: string
  lastName: string
  role: string
  sequenceNumber: number
  type: string
}

export type DataViewRACMarketingItem = {
  name: string
  slug: string
}

export type DataViewRACMarketing = {
  analysts: DataViewRACAnalyst[] | null
  countries: DataViewRACMarketingItem[]
  languages: DataViewRACMarketingItem[]
  sectors: DataViewRACMarketingItem[]
}

export type DataViewRACItem = {
  marketing: DataViewRACMarketing | null
  permalink: string
  publishedDate: string
  docType?: string
  reportType: string
  title: string
  renderLink?: ComponentType<DataViewRACRenderLinkProps>
}

export type DataViewRACRenderLinkProps = Partial<DataViewRACItem>

export type DataViewRACProps = {
  items: DataViewRACItem[]
  renderLink?: ComponentType<DataViewRACRenderLinkProps>
}

const renderLinkDefault = ({
  title,
  permalink
}: DataViewRACRenderLinkProps) => <a href={`research/${permalink}`}>{title}</a>

const sortAnalysts = (analysts: DataViewRACAnalyst[]) => {
  analysts.sort((a: DataViewRACAnalyst, b: DataViewRACAnalyst) => {
    if (a.sequenceNumber < b.sequenceNumber) return -1
    if (a.sequenceNumber > b.sequenceNumber) return 1
    return 0
  })

  return analysts
}

const renderItem = ({
  marketing,
  permalink,
  docType,
  publishedDate,
  renderLink: Link = renderLinkDefault,
  reportType,
  title
}: DataViewRACItem) => {
  const { analysts, countries, languages, sectors } = marketing || {
    analysts: [],
    countries: [],
    languages: [],
    sectors: []
  }
  const linkProps = { title, permalink }
  const isJapanese = _.get(marketing, 'language.slug') === 'ja'
  const reportTypeHeader =
    isJapanese &&
    reportType &&
    (reportType.toUpperCase() === 'NON-RATING ACTION COMMENTARY' ||
      reportType.toUpperCase() === 'RATING ACTION COMMENTARY')
      ? 'Commentary'
      : reportType

  return (
    <div
      className="column__five column__wrapper--1 column--merge-a article-data"
      key={permalink}
    >
      <div className="column column--bd-r article-data--date">
        <div className="date">
          <span className="date__1">{FormatDate(publishedDate, 'dd')}</span>
          <span className="date__2">
            {FormatDate(publishedDate, 'MMM')}
            <br />
            {FormatDate(publishedDate, 'yyyy')}
          </span>
        </div>
      </div>
      <div className="column article-data--title">
        <h3 className="heading--5">
          <Link {...linkProps} />
        </h3>
      </div>
      <div className="column article-data--type">
        <span
          className={
            _.get(marketing, 'contentAccessType.name') === 'Premium'
              ? 'premium-report__color'
              : ''
          }
        >
          {docType === 'FW' &&
          reportType &&
          reportType.toUpperCase() === 'NON-RATING ACTION COMMENTARY'
            ? 'Fitch Wire'
            : reportTypeHeader}
        </span>
      </div>
      <div className="column article-data--sector">
        <p>
          {sectors
            ? _.orderBy(sectors, ['name']).map(
                (sector: DataViewRACMarketingItem) => (
                  <Fragment key={sector.name}>
                    {sector.name}
                    <br />
                  </Fragment>
                )
              )
            : null}

          {countries
            ? _.orderBy(countries, ['name']).map(
                (country: DataViewRACMarketingItem) => (
                  <Fragment key={country.name}>
                    {country.name}
                    <br />
                  </Fragment>
                )
              )
            : null}

          {languages
            ? languages.map((language: DataViewRACMarketingItem) => (
                <Fragment key={language.name}>
                  {language.name}
                  <br />
                </Fragment>
              ))
            : null}
        </p>
      </div>
      <div className="column article-data--analyst">
        {analysts && analysts.length > 0
          ? sortAnalysts(analysts).map((analyst: DataViewRACAnalyst) => (
              <Fragment key={analyst.firstName.concat(analyst.lastName)}>
                <p>
                  {_.startCase(analyst.lastName)},{' '}
                  {_.startCase(analyst.firstName)}
                  <br />
                  {analyst.type}
                </p>
              </Fragment>
            ))
          : '-'}
      </div>
    </div>
  )
}

export const DataViewRAC = ({ items }: DataViewRACProps) => (
  <div className="column__main">
    <div className="column__five column__wrapper--1 column--merge-a article-data">
      <div className="column article-data--date">
        <h6 className="heading--6">Date</h6>
      </div>
      <div className="column article-data--title">
        <h6 className="heading--6">Title</h6>
      </div>
      <div className="column article-data--type">
        <h6 className="heading--6">Type</h6>
      </div>
      <div className="column article-data--sector">
        <h6 className="heading--6">Sector &amp; Country</h6>
      </div>
      <div className="column article-data--analyst">
        <h6 className="heading--6">Analyst(s)</h6>
      </div>
    </div>

    {items ? items.map((item: DataViewRACItem) => renderItem(item)) : null}
  </div>
)
